import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import WebUiPageWrapper from '../../components/page-wrapper';
import type { InterfaceCfg, InterfaceState } from '../settings/ports/types';

type RowData = {
  interface: number;
  name: string;
  link: string;
  statusSpeed: string;
  statusDuplex: string;
  cfgLink: string;
  cfgAutoneg: string;
  cfgSpeed: string;
  cfgDuplex: string;
  mac: string;
};

function truncateName(name: string): string {
  return name.length > 20 ? `${name.slice(0, 19)}...` : name;
}

export default function WebUiStatusInterfacesPage() {
  const [rows, setRows] = React.useState<RowData[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [cfg, state] = await Promise.all([
      postWebUiApi<InterfaceCfg[]>({
        service: 'interface',
        access: 'get',
        command: 'get interfaces',
      }),
      postWebUiApi<InterfaceState[]>({
        service: 'interface',
        access: 'get',
        command: 'get interfaces state',
      }),
    ]);

    const stateById = new Map(state.map((s) => [s.interface, s]));
    const merged: RowData[] = cfg
      .slice()
      .sort((a, b) => a.interface - b.interface)
      .map((item) => {
        const st = stateById.get(item.interface);
        const isUp = Boolean(st?.state);
        const statusSpeed = isUp ? String(st?.speed ?? '-') : '-';
        const statusDuplex = isUp ? `${st?.duplex ? 'full' : 'half'}` : '-';
        const cfgAutoneg = item.autoneg ? 'enabled' : 'disabled';
        return {
          interface: item.interface,
          name: item.name ?? '',
          link: isUp ? 'up' : 'down',
          statusSpeed,
          statusDuplex,
          cfgLink: item.up ? 'enabled' : 'disabled',
          cfgAutoneg,
          cfgSpeed: item.autoneg ? '-' : String(item.speed),
          cfgDuplex: item.autoneg ? '-' : `${item.duplex ? 'full' : 'half'}`,
          mac: st?.mac ?? '-',
        };
      });
    setRows(merged);
  }, []);

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        await reload();
        if (alive) setError(null);
      } catch (err) {
        if (alive) setError((err as Error).message);
      }
    };
    load().catch(() => {});
    const timer = window.setInterval(() => {
      load().catch(() => {});
    }, 1500);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [reload]);

  return (
    <WebUiPageWrapper titleList={['Status', 'Ports']}>
      {error && <WebUiAlert type="error" message={error} />}

      <div className="w-full">
        <h4 className="font-bold">Ports</h4>
        <table className="webui-table">
          <thead>
            <tr className="webui-table-row-head">
              <th className="text-center" colSpan={2}>
                Port
              </th>
              <th className="text-center" colSpan={3}>
                Status
              </th>
              <th className="text-center" colSpan={4}>
                Configuration
              </th>
              <th className="text-center" rowSpan={2}>
                MACs
              </th>
            </tr>
            <tr className="webui-table-row-head">
              <th className="text-center">Port</th>
              <th className="text-center">Name</th>
              <th className="text-center">Link</th>
              <th className="text-center">Speed</th>
              <th className="text-center">Duplex</th>
              <th className="text-center">Link</th>
              <th className="text-center">Auto Negotiation</th>
              <th className="text-center">Speed</th>
              <th className="text-center">Duplex</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.interface} className="webui-table-row-content">
                <td className="text-center">{r.interface}</td>
                <td className="text-center" title={r.name}>
                  {truncateName(r.name)}
                </td>
                <td className="text-center">{r.link}</td>
                <td className="text-center">{r.statusSpeed}</td>
                <td className="text-center">{r.statusDuplex}</td>
                <td className="text-center">{r.cfgLink}</td>
                <td className="text-center">{r.cfgAutoneg}</td>
                <td className="text-center">{r.cfgSpeed}</td>
                <td className="text-center">{r.cfgDuplex}</td>
                <td className="text-center">{r.mac}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </WebUiPageWrapper>
  );
}
